import {
  BLOCK_CONTRIBUTE_RESOURCE_NAME,
  BLOCK_CONTRIBUTED_LOG_RESOURCE_NAME,
  BLOCK_DISPOSE_RESOURCE_NAME,
  BLOCK_DISPOSED_LOG_RESOURCE_NAME,
  BLOCK_NEW_ORDER_RESOURCE_NAME,
  BLOCK_ORDER_RESOURCE_NAME,
  BLOCK_SLIDES_RESOURCE_NAME,
  TEAM_BLOCK_RESOURCE_NAME,
  TEAM_BLOCKS_BILLING,
  TEAM_BLOCKS_RESOURCE_NAME,
  TEAM_CREATE_BLOCK_FROM_SAMPLE_RESOURCE_NAME,
  TEAM_DATA_MANAGEMENT_BILLING,
  TEAM_DETAILS_RESOURCE_NAME,
  TEAM_DISPOSED_BLOCKS_RESOURCE_NAME,
  TEAM_PREFERENCES,
  TEAM_RESOURCE_NAME,
  TEAM_SAMPLES_TO_BLOCK_RESOURCE_NAME,
  TEAM_UNMERGE_SAMPLES_RESOURCE_NAME,
} from 'services/resources/apiConstants';
import { baseCreateApi } from 'store/apis/baseCreateApi';
import { defaultActionAsyncHandler } from 'store/utilities';
import { setTeams, updateTeam } from 'store/slices/teamsSlice';
import {
  ACTIVE_BLOCKS,
  DISPOSED_BLOCKS,
} from 'components/BlocksList/constants';
import { updateTeamDetails } from 'store/slices/userDetailsSlice';

export const teamsApi = baseCreateApi.injectEndpoints({
  endpoints: (builder) => ({
    getTeams: builder.query({
      query: () => ({ url: TEAM_RESOURCE_NAME, method: 'GET' }),
      onQueryStarted(queryArgs, { dispatch, queryFulfilled }) {
        defaultActionAsyncHandler({ dispatch, queryFulfilled }, setTeams);

        queryFulfilled
          .then(({data}) => {
            dispatch(updateTeamDetails(data));
          })
          .catch((error) => {
            // eslint-disable-next-line no-console
            console.error(
              'Error occurred while running the 2nd reducer:',
              error,
            );
          });
      },
      providesTags: ['getTeams'],
    }),
    getTeamDetails: builder.query({
      query: ({ teamUUID }) => ({
        url: TEAM_DETAILS_RESOURCE_NAME.replace(':teamUUID', teamUUID),
        method: 'GET',
      }),
      onQueryStarted(queryArgs, { dispatch, queryFulfilled }) {
        defaultActionAsyncHandler({ dispatch, queryFulfilled }, updateTeam);
      },
    }),
    getTeamBlocks: builder.query({
      query: ({ teamUUID, blockType = ACTIVE_BLOCKS, internal = false }) => {
        const blocksUrl = {
          [ACTIVE_BLOCKS]: TEAM_BLOCKS_RESOURCE_NAME,
          [DISPOSED_BLOCKS]: TEAM_DISPOSED_BLOCKS_RESOURCE_NAME,
        }[blockType];

        let url = blocksUrl.replace(':teamUUID', teamUUID);

        if (internal) url += '?internal=1';

        return {
          url,
          method: 'GET',
        };
      },
    }),
    getTeamBlocksBilling: builder.query({
      query: ({ teamUUID }) => {
        const url = TEAM_BLOCKS_BILLING.replace(':teamUUID', teamUUID);

        return {
          url,
          method: 'GET',
        };
      },
    }),
    getTeamDataManagementBilling: builder.query({
      query: ({ teamUUID }) => {
        const url = TEAM_DATA_MANAGEMENT_BILLING.replace(':teamUUID', teamUUID);

        return {
          url,
          method: 'GET',
        };
      },
    }),
    disposeBlocks: builder.mutation({
      query: ({ block_uuids }) => ({
        url: BLOCK_DISPOSE_RESOURCE_NAME,
        method: 'POST',
        body: { block_uuids },
      }),
    }),
    contributeBlocks: builder.mutation({
      query: ({ block_uuids }) => ({
        url: BLOCK_CONTRIBUTE_RESOURCE_NAME,
        method: 'POST',
        body: { block_uuids },
      }),
    }),
    disposedBlocksLog: builder.query({
      query: ({ teamUUID }) => ({
        url: BLOCK_DISPOSED_LOG_RESOURCE_NAME.replace(':teamUUID', teamUUID),
        method: 'GET',
      }),
    }),
    contributedBlocksLog: builder.query({
      query: ({ teamUUID }) => ({
        url: BLOCK_CONTRIBUTED_LOG_RESOURCE_NAME.replace(':teamUUID', teamUUID),
        method: 'GET',
      }),
    }),
    unmergeBlockSamples: builder.mutation({
      query: ({ sample_uuids }) => ({
        url: TEAM_UNMERGE_SAMPLES_RESOURCE_NAME,
        method: 'POST',
        body: { sample_uuids },
      }),
    }),
    updateBlock: builder.mutation({
      query: ({ teamUUID, blockUUID, body }) => ({
        url: TEAM_BLOCK_RESOURCE_NAME.replace(':teamUUID', teamUUID).replace(
          ':blockUUID',
          blockUUID,
        ),
        method: 'PATCH',
        body,
      }),
    }),
    mergeSamplesToBlock: builder.mutation({
      query: ({ blockUUID, samples, teamUUID }) => ({
        url: TEAM_SAMPLES_TO_BLOCK_RESOURCE_NAME.replace(':teamUUID', teamUUID),
        method: 'POST',
        body: {
          sample_uuids: samples?.map((sample) => sample.uuid),
          block_uuid: blockUUID,
        },
      }),
    }),
    createBlockFromSample: builder.mutation({
      query: ({ teamUUID, sampleUUID }) => ({
        url: TEAM_CREATE_BLOCK_FROM_SAMPLE_RESOURCE_NAME.replace(
          ':teamUUID',
          teamUUID,
        ),
        method: 'POST',
        body: { sample_uuid: sampleUUID },
      }),
    }),
    createOrderWithBlocks: builder.mutation({
      query: ({ blocksUUIDs }) => ({
        url: BLOCK_NEW_ORDER_RESOURCE_NAME,
        method: 'POST',
        body: {
          block_uuids: blocksUUIDs,
        },
      }),
    }),
    addBlocksToOrder: builder.mutation({
      query: ({ blocksUUIDs, orderUUID }) => ({
        url: BLOCK_ORDER_RESOURCE_NAME,
        method: 'POST',
        body: {
          block_uuids: blocksUUIDs,
          order_uuid: orderUUID,
        },
      }),
    }),
    getBlockSlides: builder.query({
      query: ({ blockUUID }) => ({
        url: BLOCK_SLIDES_RESOURCE_NAME.replace(':blockUUID', blockUUID),
        method: 'GET',
      }),
    }),
    getTeamPreferences: builder.query({
      query: () => ({ url: TEAM_PREFERENCES, method: 'GET' }),
    }),
    setTeamPreferences: builder.mutation({
      query: ({ body }) => ({ url: TEAM_PREFERENCES, method: 'PATCH', body }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;

          dispatch(
            teamsApi.util.updateQueryData(
              'getTeamPreferences',
              undefined,
              () => data,
            ),
          );
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to update TeamPreferences cache:', error);
        }
      },
    }),
  }),
});

export const {
  useGetTeamsQuery,
  useLazyGetTeamsQuery,
  useGetTeamDetailsQuery,
  useLazyGetTeamDetailsQuery,
  useGetTeamBlocksQuery,
  useGetTeamBlocksBillingQuery,
  useGetTeamDataManagementBillingQuery,
  useLazyGetTeamBlocksQuery,
  useUnmergeBlockSamplesMutation,
  useMergeSamplesToBlockMutation,
  useCreateBlockFromSampleMutation,
  useUpdateBlockMutation,
  useGetTeamPreferencesQuery,
  useSetTeamPreferencesMutation,
  useCreateOrderWithBlocksMutation,
  useAddBlocksToOrderMutation,
  useDisposeBlocksMutation,
  useContributeBlocksMutation,
  useDisposedBlocksLogQuery,
  useContributedBlocksLogQuery,
  useGetBlockSlidesQuery,
  useLazyGetBlockSlidesQuery,
} = teamsApi;
